import { diffWordsWithSpace } from 'diff';

const isRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
/** Resolve a dot-path on a document (e.g. `'development.sections'`). */
const getValueAtPath = (doc, path) => {
    let current = doc;
    for (const key of path.split('.')) {
        if (!isRecord(current))
            return undefined;
        current = current[key];
    }
    return current;
};
/**
 * Immutably set a dot-path on a document, creating intermediate objects as
 * needed. Only the nodes along the path are copied.
 */
const setValueAtPath = (doc, path, value) => {
    const keys = path.split('.');
    const result = { ...doc };
    let cursor = result;
    for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        const next = cursor[key];
        const copy = isRecord(next) ? { ...next } : {};
        cursor[key] = copy;
        cursor = copy;
    }
    cursor[keys[keys.length - 1]] = value;
    return result;
};
const TEXT_MERGE_CONTEXT = 40;
const MIN_TEXT_MERGE_CONTEXT = 8;
const canonical = (value) => {
    if (Array.isArray(value))
        return value.map(canonical);
    if (typeof value !== 'object' || value === null)
        return value;
    return Object.fromEntries(Object.keys(value)
        .filter((key) => key !== 'lastUpdatedAt')
        .sort()
        .map((key) => [key, canonical(value[key])]));
};
/** Deep equality that ignores key order and `lastUpdatedAt` stamps. */
const isSameContent = (a, b) => JSON.stringify(canonical(a)) === JSON.stringify(canonical(b));
const cloneValue = (value) => value === undefined ? value : JSON.parse(JSON.stringify(value));
/** The separate spots where `next` differs from `base`, by word. */
const diffHunks = (base, next) => {
    const hunks = [];
    let offset = 0;
    for (const part of diffWordsWithSpace(base, next)) {
        const last = hunks[hunks.length - 1];
        const extend = last && last.end === offset;
        if (part.added) {
            if (extend)
                last.inserted += part.value;
            else
                hunks.push({ start: offset, end: offset, inserted: part.value });
            continue;
        }
        if (part.removed) {
            if (extend)
                last.end += part.value.length;
            else
                hunks.push({
                    start: offset,
                    end: offset + part.value.length,
                    inserted: '',
                });
            offset += part.value.length;
            continue;
        }
        offset += part.value.length;
    }
    // Spots too close to tell apart by their surrounding text are applied as one.
    return hunks.reduce((merged, hunk) => {
        const previous = merged[merged.length - 1];
        if (previous && hunk.start - previous.end < TEXT_MERGE_CONTEXT) {
            previous.inserted += base.slice(previous.end, hunk.start) + hunk.inserted;
            previous.end = hunk.end;
        }
        else {
            merged.push({ ...hunk });
        }
        return merged;
    }, []);
};
/** Apply one spot of an edit onto `current`, found by its surrounding text. */
const applyHunk = (current, base, hunk) => {
    const replaced = base.slice(hunk.start, hunk.end);
    for (let context = TEXT_MERGE_CONTEXT; context >= MIN_TEXT_MERGE_CONTEXT; context -= 8) {
        const left = base.slice(Math.max(0, hunk.start - context), hunk.start);
        const right = base.slice(hunk.end, hunk.end + context);
        if (current.includes(left + hunk.inserted + right))
            return current;
        const needle = left + replaced + right;
        const at = current.indexOf(needle);
        if (at < 0)
            continue;
        if (current.indexOf(needle, at + 1) >= 0)
            return undefined;
        return (current.slice(0, at) +
            left +
            hunk.inserted +
            right +
            current.slice(at + needle.length));
    }
    return undefined;
};
/**
 * Apply a text edit (`base` to `next`) onto text that changed since `base`,
 * spot by spot: each place the edit changed is found by its surrounding text
 * and swapped in. A spot already applied is left as is; a spot that was itself
 * edited since (or is ambiguous) is skipped and counted.
 */
const rebaseTextSpots = (current, base, next) => {
    let text = current;
    let skipped = 0;
    for (const hunk of diffHunks(base, next).reverse()) {
        const applied = applyHunk(text, base, hunk);
        if (applied === undefined)
            skipped += 1;
        else
            text = applied;
    }
    return { text, skipped };
};
/** All-or-nothing `rebaseTextSpots`: undefined when any spot was skipped. */
const rebaseText = (current, base, next) => {
    const { text, skipped } = rebaseTextSpots(current, base, next);
    return skipped ? undefined : text;
};
class MergeConflicts {
    count = 0;
}
const isPlainRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
/**
 * Three-way merge of one value: apply what changed from `base` to `next` onto
 * `current`, keeping whatever else changed on `current` since `base`. A part
 * changed on both sides keeps `current` and is counted in `conflicts`.
 */
const mergeValue = (current, base, next, conflicts = new MergeConflicts()) => {
    if (isSameContent(current, base))
        return cloneValue(next);
    if (isSameContent(current, next) || isSameContent(base, next))
        return current;
    if (isPlainRecord(current) && isPlainRecord(base) && isPlainRecord(next)) {
        const result = { ...current };
        for (const key of new Set([...Object.keys(base), ...Object.keys(next)])) {
            if (key === 'lastUpdatedAt' || isSameContent(base[key], next[key]))
                continue;
            const merged = mergeValue(current[key], base[key], next[key], conflicts);
            if (merged === undefined)
                delete result[key];
            else
                result[key] = merged;
        }
        if ('lastUpdatedAt' in current && !isSameContent(result, current)) {
            result.lastUpdatedAt = new Date().toISOString();
        }
        return result;
    }
    if (typeof current === 'string' &&
        typeof base === 'string' &&
        typeof next === 'string') {
        const { text, skipped } = rebaseTextSpots(current, base, next);
        conflicts.count += skipped;
        return text;
    }
    conflicts.count += 1;
    return current;
};
/**
 * Reorder a live collection to a target id order, keeping the LIVE node for
 * each id (so concurrent live content edits survive — only position is
 * reconciled) and appending live-only nodes rather than dropping them.
 */
const reconcileCollectionOrder = (liveCollection, order) => {
    const liveById = new Map(liveCollection.map((item) => [item?.id, item]));
    const used = new Set();
    const ordered = [];
    for (const id of order) {
        const liveItem = liveById.get(id);
        if (liveItem && !used.has(id)) {
            ordered.push(liveItem);
            used.add(id);
        }
    }
    for (const liveItem of liveCollection) {
        if (!used.has(liveItem?.id)) {
            ordered.push(liveItem);
        }
    }
    return ordered;
};
/**
 * Fold a single staged change onto an entity document, driven entirely by the
 * change's self-contained `content` payload. This is the single source of
 * truth shared by the accept→live flow, the dashboard preview, the backend
 * working-copy fold, and the change diff. Every collection operation is keyed
 * by id, never index.
 */
const applySessionChangeContent = (doc, content, conflicts) => {
    // Changes persisted before the unified content model (or malformed payloads)
    // may lack a `path`; skip them rather than crash consumers.
    if (typeof content?.path !== 'string' || !content.path) {
        return doc;
    }
    if (content.op === 'set') {
        const value = content.base === undefined
            ? content.value
            : mergeValue(getValueAtPath(doc, content.path), content.base, content.value, conflicts);
        let next = setValueAtPath(doc, content.path, value);
        for (const extra of content.sets ?? []) {
            next = setValueAtPath(next, extra.path, extra.value);
        }
        return next;
    }
    const existing = getValueAtPath(doc, content.path);
    const liveCollection = Array.isArray(existing)
        ? [...existing]
        : [];
    if (content.op === 'reorder') {
        return setValueAtPath(doc, content.path, reconcileCollectionOrder(liveCollection, content.order));
    }
    if (content.op === 'delete') {
        return setValueAtPath(doc, content.path, liveCollection.filter((item) => item?.id !== content.id));
    }
    // upsert
    const node = content.node;
    if (!node) {
        return doc;
    }
    const existingIndex = liveCollection.findIndex((item) => item?.id === node.id);
    // Already live => content edit in place, preserving live position. With a
    // recorded `base`, only what the change edited is applied, so edits made to
    // the live node since the change was recorded are kept.
    if (existingIndex >= 0) {
        liveCollection[existingIndex] =
            content.base === undefined
                ? node
                : mergeValue(liveCollection[existingIndex], content.base, node, conflicts);
        return setValueAtPath(doc, content.path, liveCollection);
    }
    // New node => insert after the recorded anchor id. When the anchor is null,
    // insert at the start; when it is missing live, append at the end.
    let insertAt = liveCollection.length;
    if (content.afterId === null) {
        insertAt = 0;
    }
    else if (content.afterId !== undefined) {
        const anchorIndex = liveCollection.findIndex((item) => item?.id === content.afterId);
        insertAt = anchorIndex >= 0 ? anchorIndex + 1 : liveCollection.length;
    }
    liveCollection.splice(insertAt, 0, node);
    return setValueAtPath(doc, content.path, liveCollection);
};
/**
 * Derive the durable sub-entity locator from a change's content. Stored
 * alongside the change so navigation and opportunity capture keep working
 * after the heavy `content` payload is stripped on accept/discard.
 */
const getChangeTargetLocator = (content) => {
    if (content.op === 'delete') {
        return { path: content.path, id: content.id };
    }
    if (content.op === 'upsert') {
        const node = content.node;
        const id = typeof node?.id === 'string' ? node.id : undefined;
        return id === undefined
            ? { path: content.path }
            : { path: content.path, id };
    }
    return { path: content.path };
};
/**
 * Root document key a change writes to (e.g. `'development'`, `'name'`) —
 * the unit the accept flow persists back to Firestore.
 */
const getChangeRootKey = (content) => content.path.split('.')[0];

export { MergeConflicts, applySessionChangeContent, getChangeRootKey, getChangeTargetLocator, getValueAtPath, isSameContent, mergeValue, rebaseText, rebaseTextSpots, setValueAtPath };
//# sourceMappingURL=agent-changes.utils.esm.js.map
